import React, {
  useMemo,
  RefObject,
  ChangeEvent,
}                             from 'react';
import styled                 from 'styled-components';
import {
  Formik,
  FormikProps,
}                             from 'formik';
import {
  IInvoiceDetails,
  TInvoiceValues,
}                             from '@typings/invoiceManagement';
import {
  ICreditUnion,
  IInvoicesEmailsPayload,
}                             from '@typings/accountManagement';
import {
  EInvoiceStatus,
  EFeeType,
}                             from '@enums/invoiceManagement';
import { EAppPage }           from '@enums/common';
import { EAccountPage }       from '@enums/accountManagement';
import {
  LinkButton,
  LinksWrapper,
}                             from '@components/index';
import { ICheckboxOption }    from '@components/Checkbox/CheckboxFieldGroup';
import { Loader }             from '@components/Loader/Loader';
import { InvoiceDetailsForm } from '../components/InvoiceDetailsForm';
import { SendInvoiceModal }   from '../components/SendInvoiceModal';

const Wrapper = styled.div`
  padding: 20px 30px 60px;

  @media (min-width: 1280px) {
    padding: 20px 100px 60px;
  }
`;

const getInitialValues = (invoiceData: IInvoiceDetails): TInvoiceValues => ({
  status      : invoiceData.status ?? EInvoiceStatus.Unpaid,
  soldLots    : invoiceData.soldLots ?? [],
  invoiceFees : invoiceData.invoiceFees ?? [],
});

interface IProps {
  invoiceData              : IInvoiceDetails;
  isDownloading            : boolean;
  formikRef                : RefObject<FormikProps<TInvoiceValues>>;
  cuInfo                   : ICreditUnion | null;
  push                     : (path: string) => void;
  showSendInvoiceModal     : boolean;
  isLoadingEmails          : boolean;
  isInvoiceSending         : boolean;
  invoicesEmailOptions     : ICheckboxOption[];
  handleSendInvoice        : (values: IInvoicesEmailsPayload) => Promise<void>;
  handleSaveChanges        : (invoice: IInvoiceDetails) => Promise<void>;
  handleDownloadInvoice    : () => Promise<void>;
  openNadaUsageModal       : () => Promise<void>;
  toggleSendInvoiceModal   : () => void;
  handleDeleteSoldLot      : (soldLotId: string) => void;
  handleResetFee           : (feeType: EFeeType) => void;
  onChangeInvoiceStatus    : (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeInvoiceFeeStatus : (event: ChangeEvent<HTMLInputElement>, feeType: EFeeType) => void;
  onChangeSoldLotStatus    : (event: ChangeEvent<HTMLInputElement>, lotId: string) => void;
}

export const InvoiceDetailsPage: React.FC<IProps> = ({
  invoiceData,
  isDownloading,
  formikRef,
  isLoadingEmails,
  isInvoiceSending,
  invoicesEmailOptions,
  cuInfo,
  showSendInvoiceModal,
  push,
  handleSendInvoice,
  handleSaveChanges,
  handleDownloadInvoice,
  openNadaUsageModal,
  handleDeleteSoldLot,
  handleResetFee,
  toggleSendInvoiceModal,
  onChangeInvoiceStatus,
  onChangeInvoiceFeeStatus,
  onChangeSoldLotStatus,
}) => {
  const initialValues = useMemo<TInvoiceValues | null>(
    () => (invoiceData ? getInitialValues(invoiceData) : null),
    [invoiceData],
  );

  if (isDownloading || !invoiceData || !initialValues) {
    return <Loader />;
  }

  return (
    <Wrapper>
      <LinksWrapper>
        <LinkButton
          label   = {`Return to ${cuInfo?.name}`}
          onClick = {(): void => push(`${EAppPage.AccountManagement}/${cuInfo?.cuid}/${EAccountPage.Edit}`)}
        />
        <LinkButton
          label   = "Download this invoice"
          onClick = {handleDownloadInvoice}
        />
        <LinkButton
          label   = "Send Invoice"
          onClick = {() => toggleSendInvoiceModal()}
        />
      </LinksWrapper>

      <Formik
        innerRef      = {formikRef}
        onSubmit      = {handleSaveChanges}
        initialValues = {initialValues}
        enableReinitialize
      >
        <InvoiceDetailsForm
          invoiceData              = {invoiceData}
          handleDeleteSoldLot      = {handleDeleteSoldLot}
          handleResetFee           = {handleResetFee}
          onChangeInvoiceStatus    = {onChangeInvoiceStatus}
          onChangeInvoiceFeeStatus = {onChangeInvoiceFeeStatus}
          onChangeSoldLotStatus    = {onChangeSoldLotStatus}
          openNadaUsageModal       = {openNadaUsageModal}
        />
      </Formik>

      <SendInvoiceModal
        open                 = {showSendInvoiceModal}
        invoicesEmailOptions = {invoicesEmailOptions}
        isLoading            = {isInvoiceSending || isLoadingEmails}
        onCancel             = {toggleSendInvoiceModal}
        onSubmit             = {(values) => handleSendInvoice(values)}
      />
    </Wrapper>
  );
};
